import type { MediaAsset, MediaSourceData } from '@lumen5/luminary/core/asset/MediaAsset';

import type { BoundingBox } from 'js/src/client';
import { clamp } from 'js/src/editor/util';

export type CropDimensions = {
    assetWidth: number;
    assetHeight: number;
    viewportWidth: number;
    viewportHeight: number;
};

export type CropState = {
    zoom: number;
    xTranslatePercent: number;
    yTranslatePercent: number;
};

/** The user-controllable transform on a media or icon asset, as edited by a crop/transform session. */
export type MediaTransform = {
    xTranslation: number;
    yTranslation: number;
    scaleX: number;
    rotation: number;
    flipHorizontally: boolean;
    flipVertically: boolean;
};

export type DisplayDimensions = {
    displayMediaWidth: number;
    displayMediaHeight: number;
    displayViewportWidth: number;
    displayViewportHeight: number;
};

const MAX_VIEWPORT_WIDTH = 550;
const MAX_VIEWPORT_HEIGHT = 350;

/**
 * Gets the display dimensions used by MediaCropper for calculating zoom.
 * This matches the coordinate system used by the MediaCropper component.
 *
 * The MediaCropper displays assets within a fixed canvas size, then calculates
 * zoom and positioning relative to that display area rather than the actual viewport.
 */
export const getDisplayDimensions = (dimensions: CropDimensions): DisplayDimensions => {
    const { assetWidth, assetHeight, viewportWidth, viewportHeight } = dimensions;

    // Calculate display media dimensions (how the asset is shown in the cropper)
    let displayMediaWidth, displayMediaHeight;
    if (assetWidth / assetHeight > MAX_VIEWPORT_WIDTH / MAX_VIEWPORT_HEIGHT) {
        // width is limiting
        displayMediaWidth = MAX_VIEWPORT_WIDTH;
        displayMediaHeight = Math.floor((MAX_VIEWPORT_WIDTH * assetHeight) / assetWidth);
    } else {
        // height is limiting
        displayMediaHeight = MAX_VIEWPORT_HEIGHT;
        displayMediaWidth = Math.floor((MAX_VIEWPORT_HEIGHT * assetWidth) / assetHeight);
    }

    // Calculate display viewport dimensions (the crop area in the cropper)
    const viewportRatio = viewportWidth / viewportHeight;
    let displayViewportWidth, displayViewportHeight;
    if (viewportRatio > displayMediaWidth / displayMediaHeight) {
        // width is limiting
        displayViewportWidth = displayMediaWidth;
        displayViewportHeight = Math.floor(displayMediaWidth / viewportRatio);
    } else {
        // height is limiting
        displayViewportHeight = displayMediaHeight;
        displayViewportWidth = Math.floor(displayMediaHeight * viewportRatio);
    }

    return {
        displayMediaWidth,
        displayMediaHeight,
        displayViewportWidth,
        displayViewportHeight,
    };
};

/**
 * Calculates the zoom level needed to fill the viewport with the asset.
 * Uses the larger of the two scale ratios to ensure the asset covers the entire viewport.
 */
export const calculateFillZoom = (dimensions: CropDimensions): number => {
    const { assetWidth, assetHeight, viewportWidth, viewportHeight } = dimensions;

    if (!assetWidth || !assetHeight || !viewportWidth || !viewportHeight) {
        return 1;
    }

    const { displayMediaWidth, displayMediaHeight, displayViewportWidth, displayViewportHeight } =
        getDisplayDimensions(dimensions);

    const scaleX = displayViewportWidth / displayMediaWidth;
    const scaleY = displayViewportHeight / displayMediaHeight;
    return Math.max(scaleX, scaleY);
};

/**
 * Calculates the zoom level needed to contain the asset within the viewport.
 * Uses the smaller of the two scale ratios to ensure the asset fits entirely within the viewport.
 */
export const calculateContainZoom = (dimensions: CropDimensions): number => {
    const { assetWidth, assetHeight, viewportWidth, viewportHeight } = dimensions;

    if (!assetWidth || !assetHeight || !viewportWidth || !viewportHeight) {
        return 1;
    }

    const { displayMediaWidth, displayMediaHeight, displayViewportWidth, displayViewportHeight } =
        getDisplayDimensions(dimensions);

    const scaleX = displayViewportWidth / displayMediaWidth;
    const scaleY = displayViewportHeight / displayMediaHeight;
    return Math.min(scaleX, scaleY);
};

/**
 * Checks if the current crop state is approximately equal to a target state.
 * Uses a tolerance to account for floating point precision.
 */
export const isCropStateNearTarget = (
    current: CropState,
    target: CropState,
    tolerance: number = 0.01,
): boolean => {
    return (
        Math.abs(current.zoom - target.zoom) < tolerance &&
        Math.abs(current.xTranslatePercent - target.xTranslatePercent) < tolerance &&
        Math.abs(current.yTranslatePercent - target.yTranslatePercent) < tolerance
    );
};

/**
 * Creates a crop state for fill mode (centered, with fill zoom).
 */
export const createFillCropState = (dimensions: CropDimensions): CropState => {
    return {
        zoom: calculateFillZoom(dimensions),
        xTranslatePercent: 0,
        yTranslatePercent: 0,
    };
};

/**
 * Creates a crop state for contain mode (centered, with contain zoom).
 */
export const createContainCropState = (dimensions: CropDimensions): CropState => {
    return {
        zoom: calculateContainZoom(dimensions),
        xTranslatePercent: 0,
        yTranslatePercent: 0,
    };
};

/**
 * Checks if the aspect ratios of the asset and viewport are approximately the same.
 * When they match, fill and contain modes would be identical.
 */
export const doAspectRatiosMatch = (
    dimensions: CropDimensions,
    tolerance: number = 0.01,
): boolean => {
    const { assetWidth, assetHeight, viewportWidth, viewportHeight } = dimensions;

    if (!assetWidth || !assetHeight || !viewportWidth || !viewportHeight) {
        return false;
    }

    const assetAspectRatio = assetWidth / assetHeight;
    const viewportAspectRatio = viewportWidth / viewportHeight;

    return Math.abs(assetAspectRatio - viewportAspectRatio) < tolerance;
};

/**
 * Minimum uniform scale needed to keep rotated fill-mode media covering its frame (rotated corners
 * would otherwise expose empty space). See https://math.stackexchange.com/a/4431074
 */
export const calculateMinZoomFromRotation = (
    rotationDeg: number,
    mediaTargetWidth: number,
    mediaTargetHeight: number,
): number => {
    const angle = Math.abs((rotationDeg * Math.PI) / 180);
    const aspectRatio = Math.max(
        mediaTargetWidth / mediaTargetHeight,
        mediaTargetHeight / mediaTargetWidth,
    );
    return aspectRatio * Math.sin(angle) + Math.cos(angle);
};

export type TranslationLimit = { x: [number, number]; y: [number, number] };

export type Size = { width: number; height: number };

export type NormalizedRect = { x: number; y: number; width: number; height: number };

/**
 * The media's fit size within its frame at 1x zoom (per fill mode), before the user's scale. Falls
 * back to the viewport when the source dimensions aren't known.
 */
export const computeBaseMediaSize = (
    source: Size | null,
    viewport: Size,
    isContain: boolean,
): Size => {
    if (!source || !source.width || !source.height) {
        return viewport;
    }
    const widthRatio = viewport.width / source.width;
    const heightRatio = viewport.height / source.height;
    const ratio = isContain ? Math.min(widthRatio, heightRatio) : Math.max(widthRatio, heightRatio);
    return { width: source.width * ratio, height: source.height * ratio };
};

// The rendered media size in frame pixels: the fit base size times the per-axis user scale.
export const computeMediaRenderSize = (
    baseMediaSize: Size,
    scaleX: number,
    scaleY: number,
): Size => ({
    width: baseMediaSize.width * scaleX,
    height: baseMediaSize.height * scaleY,
});

/**
 * The media's rendered rectangle, normalized to the scene, so the gizmo box can follow the asset as
 * it's zoomed/panned. `frame` is the fixed frame's normalized rect; `tx`/`ty` are clamped translation
 * fractions of the frame. Centre moves opposite the translation (the media follows the drag).
 */
export const computeMediaOverlayRect = (
    frame: NormalizedRect,
    renderSize: Size,
    viewport: Size,
    tx: number,
    ty: number,
): NormalizedRect => {
    if (viewport.width <= 0 || viewport.height <= 0) {
        return frame;
    }
    const width = frame.width * (renderSize.width / viewport.width);
    const height = frame.height * (renderSize.height / viewport.height);
    const centerX = frame.x + frame.width / 2 - tx * frame.width;
    const centerY = frame.y + frame.height / 2 - ty * frame.height;
    return { x: centerX - width / 2, y: centerY - height / 2, width, height };
};

const GHOST_LAYER_TILES = 3;

const GHOST_LAYER_SCENE_RECT: NormalizedRect = {
    x: 1 / GHOST_LAYER_TILES,
    y: 1 / GHOST_LAYER_TILES,
    width: 1 / GHOST_LAYER_TILES,
    height: 1 / GHOST_LAYER_TILES,
};

export const toGhostLayerRect = (sceneRect: NormalizedRect): NormalizedRect => ({
    x: GHOST_LAYER_SCENE_RECT.x + sceneRect.x * GHOST_LAYER_SCENE_RECT.width,
    y: GHOST_LAYER_SCENE_RECT.y + sceneRect.y * GHOST_LAYER_SCENE_RECT.height,
    width: sceneRect.width * GHOST_LAYER_SCENE_RECT.width,
    height: sceneRect.height * GHOST_LAYER_SCENE_RECT.height,
});

const GHOST_CUTOUT_TILE_PX = 512;

export const buildGhostCutoutCanvas = ({
    clipCoverage,
}: {
    clipCoverage: CanvasImageSource | null;
}): HTMLCanvasElement => {
    const canvas = document.createElement('canvas');
    canvas.width = GHOST_CUTOUT_TILE_PX * GHOST_LAYER_TILES;
    canvas.height = GHOST_CUTOUT_TILE_PX * GHOST_LAYER_TILES;
    const context = canvas.getContext('2d');
    if (!context) {
        return canvas;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    const scene = {
        x: GHOST_LAYER_SCENE_RECT.x * canvas.width,
        y: GHOST_LAYER_SCENE_RECT.y * canvas.height,
        width: GHOST_LAYER_SCENE_RECT.width * canvas.width,
        height: GHOST_LAYER_SCENE_RECT.height * canvas.height,
    };

    // `destination-out` keys off the source's alpha, so the clip shape's own coverage carves the cutout.
    context.globalCompositeOperation = 'destination-out';
    if (clipCoverage) {
        context.drawImage(clipCoverage, scene.x, scene.y, scene.width, scene.height);
    } else {
        // Resampling the map up to the layer softens this edge, which would leave a hair of ghost
        // lying over the scene. Overshooting by a pixel hides that hair instead.
        context.fillRect(scene.x - 1, scene.y - 1, scene.width + 2, scene.height + 2);
    }
    context.globalCompositeOperation = 'source-over';

    return canvas;
};

/**
 * New pan translation (as fractions of the media target resolution) for a screen-pixel drag over the
 * frame. The media follows the cursor, so the translation moves opposite the drag. Clamped to `limit`.
 */
export const computeCropPan = (
    start: { xTranslation: number; yTranslation: number },
    delta: { dx: number; dy: number },
    frame: { width: number; height: number },
    limit: TranslationLimit,
): { xTranslation: number; yTranslation: number } => {
    if (frame.width <= 0 || frame.height <= 0) {
        return { xTranslation: start.xTranslation, yTranslation: start.yTranslation };
    }
    return {
        xTranslation: clamp(limit.x, start.xTranslation - delta.dx / frame.width),
        yTranslation: clamp(limit.y, start.yTranslation - delta.dy / frame.height),
    };
};

/**
 * Proportional scale ratio for dragging a resize handle, aware of box rotation. The handle's outward
 * normal (centre → handle, in the box's unrotated frame) is rotated into screen space and the drag is
 * projected onto it, so pulling perpendicular to a rotated edge scales and sliding along it does
 * nothing. `halfW`/`halfH` are the unrotated half-extents; `signX`/`signY` point outward.
 */
export const computeHandleScaleRatio = (
    handle: { signX: number; signY: number },
    halfW: number,
    halfH: number,
    angleRad: number,
    delta: { dx: number; dy: number },
): number => {
    const offsetX = handle.signX * halfW;
    const offsetY = handle.signY * halfH;
    const refDist = Math.hypot(offsetX, offsetY);
    if (refDist <= 0) {
        return 1;
    }
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    const normalX = (offsetX * cos - offsetY * sin) / refDist;
    const normalY = (offsetX * sin + offsetY * cos) / refDist;
    const projection = delta.dx * normalX + delta.dy * normalY;
    return (refDist + projection) / refDist;
};

export const cropToBoundingBox = (
    faceDetection: BoundingBox,
    asset: MediaAsset,
    sourceData: MediaSourceData,
    paddingPercent: number = 0.1,
) => {
    // all the values in the BoundingBox are normalized to the range [0, 1] (percentages of the overall height/width)

    // do not let the scale be less than 1
    const xScale = Math.max(1 / (faceDetection.width + paddingPercent * 2), 1);
    const yScale = Math.max(1 / (faceDetection.height + paddingPercent * 2), 1);

    // out of the two scales, use the smaller one to avoid over-zooming in
    const scale = Math.min(xScale, yScale);
    asset.scaleX = scale;
    asset.scaleY = scale;

    const mediaAssetRatio = asset.mediaTargetResolution.width / asset.mediaTargetResolution.height;
    const sourceRatio = sourceData.width / sourceData.height;

    // by default, when the source is placed into the media asset, the source will be
    // scaled to match either the height or the width of the media asset, depending
    // on the aspect ratios (assuming fillMode is cover). This lets us calculate the
    // source width and height before scale.
    const isHeightBound = mediaAssetRatio < sourceRatio;

    const sourceHeight = isHeightBound
        ? asset.mediaTargetResolution.height
        : asset.mediaTargetResolution.width / sourceRatio;
    const sourceWidth = isHeightBound
        ? asset.mediaTargetResolution.height * sourceRatio
        : asset.mediaTargetResolution.width;

    // now we can scale the source dimensions
    const scaledSourceHeight = sourceHeight * scale;
    const scaledSourceWidth = sourceWidth * scale;

    // and we can find the midpoint of the face in the scaled source
    const midpointXPercent = faceDetection.x + faceDetection.width / 2;
    const midpointYPercent = faceDetection.y + faceDetection.height / 2;

    const midpointX = midpointXPercent * scaledSourceWidth;
    const midpointY = midpointYPercent * scaledSourceHeight;

    // xTranslation and yTranslation are expressed in percentages of the mediaTargetResolution.
    // So, for example, if the xTranslation is 1 and the mediaTargetResolution is 100, this will
    // result in a 100px translation right. Negative values translate left.
    // So we need to convert the midpointX and midpointY to percentages of the mediaTargetResolution.
    asset.xTranslation = (midpointX - scaledSourceWidth / 2) / asset.mediaTargetResolution.width;
    asset.yTranslation = (midpointY - scaledSourceHeight / 2) / asset.mediaTargetResolution.height;
};
